import { hashKey } from "@tanstack/react-query";
import { useState } from "react";

import { notificationQueryKeys } from "../api/query-keys";
import { NOTIFICATION_BULK_MAX_IDS } from "../constants";
import type { NotificationActionTarget, NotificationData, NotificationFilters, NotificationSelection } from "../types";
import { isCreatedAtOrBefore } from "../utils";

type NotificationId = NotificationData["id"];

const NO_SELECTION: NotificationSelection = { mode: "none" };

type UseNotificationSelectionParams = {
  filters: NotificationFilters;
  notifications: NotificationData[];
  totalRecords: number;
};

function toIdsSelection(ids: Set<NotificationId>, anchorId: NotificationId): NotificationSelection {
  if (ids.size === 0) return NO_SELECTION;
  return { mode: "ids", ids, anchorId };
}

function getRangeIds(notifications: NotificationData[], anchorId: NotificationId, notificationId: NotificationId) {
  const anchorIndex = notifications.findIndex((notification) => notification.id === anchorId);
  const index = notifications.findIndex((notification) => notification.id === notificationId);
  if (anchorIndex === -1 || index === -1) return [notificationId];
  const [start, end] = anchorIndex < index ? [anchorIndex, index] : [index, anchorIndex];
  return notifications.slice(start, end + 1).map((notification) => notification.id);
}

export function useNotificationSelection({ filters, notifications, totalRecords }: UseNotificationSelectionParams) {
  const filtersKey = hashKey(notificationQueryKeys.page(filters));
  const [state, setState] = useState<{ filtersKey: string; selection: NotificationSelection }>({
    filtersKey,
    selection: NO_SELECTION,
  });
  const selection = state.filtersKey === filtersKey ? state.selection : NO_SELECTION;

  const setSelection = (next: NotificationSelection) => setState({ filtersKey, selection: next });

  const isChecked = (notification: NotificationData) => {
    if (selection.mode === "ids") return selection.ids.has(notification.id);
    if (selection.mode === "matching") {
      return (
        !selection.excludedIds.has(notification.id) &&
        isCreatedAtOrBefore(notification.createdAt, selection.createdAtOrBefore)
      );
    }
    return false;
  };

  const checkedLoadedIds = notifications.filter(isChecked).map((notification) => notification.id);
  const uncheckedLoadedCount = notifications.length - checkedLoadedIds.length;
  const isEveryLoadedChecked = notifications.length > 0 && uncheckedLoadedCount === 0;
  const mode = selection.mode === "ids" && checkedLoadedIds.length === 0 ? NO_SELECTION.mode : selection.mode;

  const toggle = (notificationId: NotificationId, extendRange: boolean) => {
    if (selection.mode === "matching") {
      const excludedIds = new Set(selection.excludedIds);
      if (excludedIds.has(notificationId)) excludedIds.delete(notificationId);
      else excludedIds.add(notificationId);
      setSelection({ ...selection, excludedIds });
      return;
    }
    const ids = new Set(checkedLoadedIds);
    if (extendRange && selection.mode === "ids") {
      const rangeIds = getRangeIds(notifications, selection.anchorId, notificationId);
      setSelection(toIdsSelection(new Set([...ids, ...rangeIds]), notificationId));
      return;
    }
    if (ids.has(notificationId)) ids.delete(notificationId);
    else ids.add(notificationId);
    setSelection(toIdsSelection(ids, notificationId));
  };

  const toggleAllLoaded = () => {
    const [first] = notifications;
    if (isEveryLoadedChecked || !first) {
      setSelection(NO_SELECTION);
      return;
    }
    if (selection.mode === "matching" && selection.excludedIds.size > 0) {
      setSelection({ ...selection, excludedIds: new Set() });
      return;
    }
    setSelection(toIdsSelection(new Set(notifications.map((notification) => notification.id)), first.id));
  };

  const selectAllMatching = () => {
    const [newest] = notifications;
    if (!newest) return;
    setSelection({ mode: "matching", createdAtOrBefore: newest.createdAt, excludedIds: new Set() });
  };

  const clear = () => setSelection(NO_SELECTION);

  const getTarget = (): NotificationActionTarget | undefined => {
    if (mode === "ids") return { notificationIds: checkedLoadedIds };
    if (selection.mode === "matching") {
      return {
        filter: filters,
        createdAtOrBefore: selection.createdAtOrBefore,
        excludedIds: [...selection.excludedIds],
      };
    }
    return undefined;
  };

  const getHeaderState = (): boolean | "indeterminate" => {
    if (mode === "none") return false;
    if (isEveryLoadedChecked) return true;
    return "indeterminate";
  };

  const selectedCount =
    mode === "matching" ? totalRecords - uncheckedLoadedCount : checkedLoadedIds.length;

  return {
    mode,
    isChecked,
    toggle,
    toggleAllLoaded,
    selectAllMatching,
    clear,
    target: getTarget(),
    headerState: getHeaderState(),
    selectedCount,
    loadedCount: notifications.length,
    canSelectAllMatching: mode === "ids" && isEveryLoadedChecked && notifications.length < totalRecords,
    hasUnreadSelected: notifications.some((notification) => notification.readAt === null && isChecked(notification)),
    excludedUnreadCount:
      selection.mode === "matching"
        ? notifications.filter((notification) => notification.readAt === null && selection.excludedIds.has(notification.id))
          .length
        : 0,
    exceedsIdLimit:
      (mode === "ids" && checkedLoadedIds.length > NOTIFICATION_BULK_MAX_IDS) ||
      (selection.mode === "matching" && selection.excludedIds.size > NOTIFICATION_BULK_MAX_IDS),
  };
}
